import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import "../UsageAnalyticsPage/UsageAnalyticsPage.css";
import {
  fetchInsightsReport,
  fetchInsightsReportList,
  type InsightsReport,
  type InsightsReportSummary,
  type FailureCategory,
} from "../../client/UsageAnalyticsClient";
import {
  fetchFailedCases,
  type FailedCasesResponse,
} from "../../client/EvalApiClient";
import { evalApi } from "../../client/EvalApiClient";
import { ConversationTrace } from "../EvalReportsPage/tabs/ContinuousEvalTab";
import { loadErrorText, toLoadError, type LoadError } from "../../utils/loadError";
import { evalAgentLabel } from "../../agents";
import AskEvalAgent, { askValue } from "../../components/AskEvalAgent/AskEvalAgent";

// Agent selection lives in EvalLayout's sidebar and arrives via
// <Outlet context={{ agent }} />; used for the API calls and the page title.

interface EvalOutletContext {
  agent: string;
}

export default function InsightsPage() {
  const { t } = useTranslation("eval");
  const { agent } = useOutletContext<EvalOutletContext>();
  const agentLabel = evalAgentLabel(agent);
  const [report, setReport] = useState<InsightsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LoadError | null>(null);
  const [insightsTab, setInsightsTab] = useState<"overview" | "optimization" | "failures">("overview");
  const [reportList, setReportList] = useState<InsightsReportSummary[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  // Failure cases are fetched here (not inside the panel) so the tab can be
  // hidden when there are none.
  const [cases, setCases] = useState<FailedCasesResponse | null>(null);
  const [casesLoading, setCasesLoading] = useState(true);
  const [casesError, setCasesError] = useState<LoadError | null>(null);

  const loadInsights = useCallback(async (date?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchInsightsReport(agent, date || undefined);
      setReport(data);
    } catch (err: unknown) {
      setError(toLoadError(err, "insights.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [agent]);

  // When agent changes: reset date, reload report list, fetch latest report
  useEffect(() => {
    setSelectedDate('');
    fetchInsightsReportList(agent).then(setReportList).catch(() => setReportList([]));
    loadInsights();
  }, [agent, loadInsights]);

  useEffect(() => {
    let cancelled = false;
    // Keep the previous result (and error) until the new one arrives, so the
    // Failures tab doesn't vanish — and bounce the user to Overview — while it
    // refetches after a date or agent change.
    setCasesLoading(true);
    fetchFailedCases(selectedDate || undefined, agent)
      .then((resp) => { if (!cancelled) { setCases(resp); setCasesError(null); } })
      .catch((err: unknown) => {
        if (!cancelled) { setCases(null); setCasesError(toLoadError(err, "insights.casesFailed")); }
      })
      .finally(() => { if (!cancelled) setCasesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDate, agent]);

  // Tabs with no data are hidden. The self-hosted intent pipeline does not
  // produce failure analysis, so Optimization is usually empty. Failures shows
  // when the latest fetch returned at least one case (the previous result
  // decides while a refetch is in flight), and stays visible on a load error
  // so the error is not swallowed.
  const showOptimization = (report?.failureCategories.length ?? 0) > 0;
  const showFailures = casesError !== null || (cases?.failed_cases.length ?? 0) > 0;
  const activeTab =
    (insightsTab === "optimization" && !showOptimization) || (insightsTab === "failures" && !showFailures)
      ? "overview"
      : insightsTab;

  return (
    <div className="eval-usage">
      {/* Header — matches Usage / Tasks / Reports: title + last-update + selector */}
      <div className="eval-usage__header">
        <div className="eval-usage__header-left">
          <h1 className="eval-usage__page-title">{t("insights.title", { agent: agentLabel })}</h1>
          <p className="eval-usage__page-subtitle">{t("shared.lastUpdate", { time: report?.reportDate ?? '—' })}</p>
        </div>
        {reportList.length > 0 && (
          <select
            className="eval-usage__select"
            value={selectedDate}
            onChange={(e) => { setSelectedDate(e.target.value); loadInsights(e.target.value); }}
            aria-label={t("insights.reportDate")}
          >
            <option value="">{t("insights.latestReport")}</option>
            {reportList.map((r) => (
              <option key={r.reportDate} value={r.reportDate}>
                {t("insights.reportOption", { date: r.reportDate, n: r.sessionsAnalyzed })}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Content */}
      {loading && <div className="eval-usage__loading">{t("insights.loading")}</div>}
      {error && <div className="eval-usage__error">{loadErrorText(error, t)}</div>}
      {!loading && !error && !report && (
        <div className="eval-usage__loading">{t("insights.noData")}</div>
      )}
      {!loading && !error && report && (
        <>
          {/* Sub-tabs (bar hidden when Overview is the only one) */}
          {(showOptimization || showFailures) && (
            <div className="eval-usage__tabs">
              <button
                className={`eval-usage__tab${activeTab === "overview" ? " eval-usage__tab--active" : ""}`}
                onClick={() => setInsightsTab("overview")}
              >
                {t("insights.tabOverview")}
              </button>
              {showOptimization && (
                <button
                  className={`eval-usage__tab${activeTab === "optimization" ? " eval-usage__tab--active" : ""}`}
                  onClick={() => setInsightsTab("optimization")}
                >
                  {t("insights.tabOptimization")}
                </button>
              )}
              {showFailures && (
                <button
                  className={`eval-usage__tab${activeTab === "failures" ? " eval-usage__tab--active" : ""}`}
                  onClick={() => setInsightsTab("failures")}
                >
                  {t("insights.tabFailures")}
                </button>
              )}
            </div>
          )}

          {activeTab === "overview" && (
            <div className="insights-intent-list">
              {report.intentRanking.map((item) => (
                <div key={item.rank} className="insights-intent-card">
                  <div className="insights-intent-card__header">
                    <span className={`insights-intent-card__badge insights-intent-card__badge--${item.rank <= 3 ? item.rank : 'default'}`}>
                      {String(item.rank).padStart(2, '0')}
                    </span>
                    <h4 className="insights-intent-card__title">{item.intent}</h4>
                    <span className="insights-intent-card__sessions">{t("insights.intentSessions", { n: item.sessions })}</span>
                    <AskEvalAgent
                      question={t("askAgent.insights.intent", {
                        agent: agentLabel,
                        date: report.reportDate || "—",
                        intent: item.intent,
                        rank: item.rank,
                        sessions: item.sessions,
                      })}
                    />
                  </div>
                  <p className="insights-intent-card__label">{t("insights.description")}</p>
                  <p className="insights-intent-card__desc">{item.description}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === "optimization" && (
            <div className="eval-usage__insights-section">
              <FailureDrillDown
                categories={report.failureCategories}
                ask={{ agent: agentLabel, date: report.reportDate || "—" }}
              />
            </div>
          )}

          {activeTab === "failures" && (
            <div className="eval-usage__insights-section">
              <FailureCasesPanel data={cases} loading={casesLoading} error={casesError} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Failure Cases Panel Component ────────────────────────────────────────────

// ── Inline Session Conversation Viewer ────────────────────────────────────────

function SessionConversation({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation("eval");
  const [messages, setMessages] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LoadError | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (loaded) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await evalApi.getSessionDetail(sessionId);
      setMessages(resp.conversation || []);
      setLoaded(true);
    } catch (err: unknown) {
      setError(toLoadError(err, "insights.conversationFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <details style={{ marginTop: 8 }} onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) load(); }}>
      <summary style={{
        fontSize: 11, color: "#6b7280", cursor: "pointer", fontWeight: 600,
      }}>
        {t("insights.conversation")}
      </summary>
      <div style={{ marginTop: 8 }}>
        {loading && <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>{t("insights.loadingConversation")}</div>}
        {error && <div style={{ fontSize: 12, color: "#dc2626", padding: "8px 0" }}>{loadErrorText(error, t)}</div>}
        {messages && messages.length === 0 && (
          <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>{t("insights.noConversation")}</div>
        )}
        {messages && messages.length > 0 && (
          <ConversationTrace messages={messages} />
        )}
      </div>
    </details>
  );
}

function FailureCasesPanel({
  data,
  loading,
  error,
}: {
  data: FailedCasesResponse | null;
  loading: boolean;
  error: LoadError | null;
}) {
  const { t } = useTranslation("eval");

  const [expandedIdx, setExpandedIdx] = useState<Set<number>>(new Set());

  const toggleExpand = (idx: number) => {
    setExpandedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (loading) return <div className="eval-usage__loading">{t("insights.loadingCases")}</div>;
  if (error) return <div className="eval-usage__error">{loadErrorText(error, t)}</div>;

  return (
    <div>
      {/* Run link */}
      {data?.run_id && (
        <div style={{ marginBottom: 12, fontSize: 13 }}>
          <span style={{ color: "#6b7280" }}>{t("insights.sourceRun")} </span>
          <a
            href={`/eval/reports?tab=continuous&run=${data.run_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#2563eb", textDecoration: "none", fontFamily: "monospace" }}
          >
            {data.run_id}
          </a>
          <AskEvalAgent question={t("askAgent.insights.failures", { runId: data.run_id })} />
        </div>
      )}
      {/* Cases list */}
      {!data || data.failed_cases.length === 0 ? (
        <div className="eval-usage__loading">{t("insights.noCases")}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {data.failed_cases.map((fc, idx) => (
            <div
              key={idx}
              style={{
                border: expandedIdx.has(idx) ? "1.5px solid #3b82f6" : "1px solid #e5e7eb",
                borderRadius: 8,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              {/* Card Header */}
              <div
                onClick={() => toggleExpand(idx)}
                style={{
                  padding: "14px 20px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  {expandedIdx.has(idx) ? "▼" : "▶"}
                </span>
                <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
                  {fc.evaluator.replace("Builtin.", "")}
                </span>
                <span style={{ fontSize: 13, color: "#dc2626", fontWeight: 500 }}>
                  {t("insights.score", { score: fc.score })}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{
                  padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                  background: "#dc2626", color: "#fff",
                }}>
                  {fc.severity}
                </span>
                <span style={{
                  padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                  background: "#dc2626", color: "#fff",
                }}>
                  {fc.verdict}
                </span>
                <span style={{
                  padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                  background: "#dc2626", color: "#fff",
                }}>
                  {fc.action}
                </span>
                <AskEvalAgent
                  question={t("askAgent.insights.failure", {
                    runId: data.run_id || "—",
                    evaluator: fc.evaluator.replace("Builtin.", ""),
                    sessionId: fc.session_id || "—",
                    traceId: fc.trace_id || "—",
                    score: askValue(fc.score),
                    verdict: fc.verdict || "—",
                    severity: fc.severity || "—",
                    action: fc.action || "—",
                    rootCause: fc.root_cause || "—",
                  })}
                />
              </div>

              {/* Card Body (expanded) */}
              {expandedIdx.has(idx) && (
                <div style={{ padding: "0 20px 20px" }}>
                  {/* Meta line */}
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 20 }}>
                    {t("insights.session")} <a
                      href={`/eval/reports?tab=continuous&run=${data.run_id}&session=${fc.session_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#6b7280", fontFamily: "monospace" }}
                    >{fc.session_id}</a>
                    {fc.trace_id && <span style={{ marginLeft: 16 }}>{t("insights.trace", { id: fc.trace_id })}</span>}
                    {fc.span_id && <span style={{ marginLeft: 16 }}>{t("insights.span", { id: fc.span_id })}</span>}
                  </div>

                  {/* Root Cause */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{
                      fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                      color: "#dc2626", marginBottom: 6,
                    }}>
                      {t("insights.rootCause")}
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.7, color: "#1f2937" }}>
                      {fc.root_cause}
                    </div>
                  </div>

                  {/* Suggestion */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{
                      fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                      color: "#dc2626", marginBottom: 6,
                    }}>
                      {t("insights.suggestion")}
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.7, color: "#1f2937" }}>
                      {fc.suggestion}
                    </div>
                  </div>

                  {/* Original Evaluator Explanation */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{
                      fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                      color: "#6b7280", marginBottom: 6,
                    }}>
                      {t("insights.explanation")}
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.6, color: "#4b5563" }}>
                      {fc.evaluator_explanation}
                    </div>
                  </div>

                  {/* Session Conversation (lazy-loaded) */}
                  <SessionConversation sessionId={fc.session_id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Failure Drill-Down Component ─────────────────────────────────────────────

function FailureDrillDown({ categories, ask }: { categories: FailureCategory[]; ask: { agent: string; date: string } }) {
  const { t } = useTranslation("eval");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [expandedRoots, setExpandedRoots] = useState<Set<string>>(new Set());

  if (!categories.length) {
    return <div className="eval-usage__loading">{t("insights.noOptimization")}</div>;
  }

  const selected = categories[selectedIdx];

  const toggleRoot = (key: string) => {
    setExpandedRoots((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="optimization-split">
      {/* Left: category list */}
      <div className="optimization-split__left">
        <h3 className="optimization-split__heading">
          {t("insights.optimizationCategory")}
          <AskEvalAgent question={t("askAgent.insights.categories", ask)} />
        </h3>
        <div className="optimization-split__list">
          {categories.map((cat, idx) => (
            <button
              key={idx}
              className={`optimization-split__item${idx === selectedIdx ? ' optimization-split__item--active' : ''}`}
              onClick={() => { setSelectedIdx(idx); setExpandedRoots(new Set()); }}
            >
              <span className="optimization-split__item-name">{cat.name}</span>
              <span className="optimization-split__item-sessions">{t("insights.sessionsOf", { n: cat.sessions, total: cat.totalSessions })}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Right: detail panel */}
      <div className="optimization-split__right">
        <h3 className="optimization-split__detail-title">
          {selected.name}
          <AskEvalAgent
            question={t("askAgent.insights.category", {
              ...ask,
              category: selected.name,
              sessions: selected.sessions,
              total: selected.totalSessions,
              rootCauses: selected.rootCauseCount,
            })}
          />
        </h3>

        {/* Summary stats */}
        <div className="optimization-split__stats">
          <div className="optimization-split__stat">
            <span className="optimization-split__stat-label">{t("insights.affectedSessions")}</span>
            <span className="optimization-split__stat-value">{t("insights.sessionsOf", { n: selected.sessions, total: selected.totalSessions })}</span>
          </div>
          <div className="optimization-split__stat">
            <span className="optimization-split__stat-label">{t("insights.rootCauses")}</span>
            <span className="optimization-split__stat-value">{selected.rootCauseCount}</span>
          </div>
        </div>

        {/* Root causes accordion */}
        <div className="optimization-split__roots">
          {selected.subCategories.flatMap((sub, si) =>
            sub.rootCauses.map((rc, ri) => {
              const key = `${si}-${ri}`;
              const isOpen = expandedRoots.has(key);
              return (
                <div key={key} className="optimization-split__root-card">
                  {/* The ask button sits beside the header, not in it: a button can't hold one. */}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <button
                      className="optimization-split__root-header"
                      style={{ flex: 1 }}
                      onClick={() => toggleRoot(key)}
                    >
                      <span className="optimization-split__root-arrow">
                        {isOpen ? "▼" : "▶"}
                      </span>
                      <span className="optimization-split__root-name">
                        {t("insights.rootCauseName", { name: rc.name })}
                      </span>
                    </button>
                    <AskEvalAgent
                      question={t("askAgent.insights.rootCause", {
                        category: selected.name,
                        rootCause: rc.name,
                        recommendation: rc.recommendation || "—",
                      })}
                    />
                  </div>
                  {isOpen && (
                    <div className="optimization-split__root-body">
                      <p>{t("insights.recommendation", { text: rc.recommendation })}</p>
                    </div>
                  )}
                </div>
              );
            }),
          )}
        </div>
      </div>
    </div>
  );
}
